var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var Field_1;
import React, { Component, createRef } from 'react';
import get from 'lodash.get';
import { Alignment, Placement, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType, Container } from '@philips-internal/oswald-ui-core';
import { any, arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string, validators } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, ValidateEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { EditorService, MessageService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { REQUIRED } from '@philips-internal/oswald-ui-model/lib/validators/required.js';
import { classNames, defaultClasses, enumValues, getLostProps, isContentfulString, isEmptyObject, isEmptyOrBlankString, isEqualShallow, isFunction, isValidWidth, noop, propagateProps, shallowCompare } from '@philips-internal/oswald-ui-utils';
import { ReactComponent as HideIcon } from '../../../resources/assets/components/DLS_Hide.svg';
import { ReactComponent as InformationCircleIcon } from '../../../resources/assets/components/DLS_InformationCircle.svg';
import { ReactComponent as ShowIcon } from '../../../resources/assets/components/DLS_Show.svg';
import { findChildWithPrettyValueFormatter } from '../../utils/find-child-with-pretty-value-formatter.js';
import { FormContext } from '../form/context/form-context.js';
import { TooltipTrigger } from '../tooltip/tooltip-constants.js';
import { Tooltip } from '../tooltip/tooltip.js';
import { ValidationList } from '../validation-list/validation-list.js';
import { FieldProvider } from './context/field-context.js';
import FieldAPI from './field-api.js';
import { FieldOrientation, FieldVisibility } from './field-constants.js';
import './field.css';
// validators should preferably be out of this list, but its contents may change the result of isRequired
const shallowCompareProps = [
    'alwaysShowLabel',
    'children',
    'className',
    'dependsOn',
    'discloseableSensitiveData',
    'editing',
    'ellipsis',
    'entry',
    'hideLabel',
    'label',
    'localValidators',
    'reverse',
    'order',
    'uuid',
    'validators',
    'sensitiveData',
    'visibility',
    'visible',
    'width',
];
const shallowCompareState = [
    'focused',
    'maskSensitiveValues',
    'needsValidation',
    'showValidations',
    'touched',
    'valid',
];
const FieldContainer = {
    acceptedTypes: [
        ComponentType.BEHAVIOR,
        ComponentType.DATA_DISPLAY,
        ComponentType.DATA_ENTRY,
    ],
    name: '',
};
let Field = Field_1 = class Field extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            __validation: {
                validate: (value, showValidations, notify) => {
                    return this.validate(value, showValidations, notify);
                },
            },
            focused: false,
            maskSensitiveValues: true,
            needsValidation: false,
            runOnFocusValidations: true,
            showValidations: false,
            touched: false,
        };
        this.fieldProviderRef = createRef();
        this.unlistenReset = noop;
        this.unlistenValidate = noop;
        this.onContextReset = () => {
            this.setState({
                focused: false,
                needsValidation: false,
                runOnFocusValidations: true,
                showValidations: false,
                touched: false,
            });
            this.fieldProviderRef.current?.resetChildValidations();
        };
        this.onContextValidate = () => {
            if (!this.props.visible) {
                return;
            }
            this.setState({ touched: true }, () => this.validate(this.getValue(), true, false));
        };
        this.onChange = ({ oldValue, prettyValue, value }) => {
            const { entry, label, onChange, validatedOnChange } = this.props;
            const callback = () => {
                if (validatedOnChange) {
                    this.validate(value, true);
                }
                if (isFunction(onChange)) {
                    const event = new ChangeEvent({
                        entry,
                        oldValue,
                        prettyValue: Field_1.formatPrettyValue({ label, value: prettyValue }),
                        value,
                    });
                    onChange(event);
                }
            };
            this.setState({ needsValidation: true, touched: true }, callback);
        };
        this.onBlur = (event) => {
            const { onBlur } = this.props;
            this.validate(this.getValue(), true);
            isFunction(onBlur) && onBlur(event);
            this.setState({ focused: false, needsValidation: true, touched: true });
        };
        this.onFocus = () => {
            this.setState({ focused: true, needsValidation: true }, () => {
                this.validateOnFocus();
            });
        };
        this.onKeyDown = (event) => {
            const { onKeyDown } = this.props;
            if (isFunction(onKeyDown)) {
                onKeyDown(event);
            }
        };
        this.onKeyUp = (event) => {
            const { onKeyUp, validatedOnChange } = this.props;
            const { showValidations } = this.state;
            if (showValidations && !validatedOnChange) {
                this.resetValidations();
            }
            isFunction(onKeyUp) && onKeyUp(event);
        };
        this.validateOnFocus = () => {
            if (!this.context) {
                return;
            }
            const { entry, onValidate } = this.props;
            const { showValidations, runOnFocusValidations } = this.state;
            if (showValidations || !runOnFocusValidations) {
                return;
            }
            const [valid, validations] = this.context.validateEntry(entry, this.getValue(), true);
            const callback = () => {
                if (isFunction(onValidate)) {
                    const event = new ValidateEvent({ entry, valid, validations });
                    onValidate(event);
                }
            };
            this.setState({ runOnFocusValidations: false, showValidations: true, valid }, callback);
        };
        this.togglePrivateValueMask = (event) => {
            event.stopPropagation();
            this.setState(prevState => ({ maskSensitiveValues: !prevState.maskSensitiveValues }));
        };
    }
    /**
    * Prettifies a given value with the 'formatPrettyValue' method of one of the provided children.
    *
    * @param properties - Properties to generate pretty value.
    * @param [properties.children] - Used to find a 'formatPrettyValue' method.
    * @param [properties.label] - Label that will be added to the final value.
    * @param [properties.value] - Value to be formatted.
    */
    static formatPrettyValue(properties) {
        const { children, label = Field_1.defaultProps.label, value } = properties;
        const fieldPrettyValueFormatter = ({ label, value }) => {
            return isEmptyOrBlankString(label)
                ? String(value)
                : `${MessageService.translate(label)}: ${value}`;
        };
        if (!children) {
            return fieldPrettyValueFormatter({ label, value: String(value) });
        }
        const child = findChildWithPrettyValueFormatter(children);
        if (!child) {
            return fieldPrettyValueFormatter({ label, value });
        }
        const childFormattedValue = child.type.formatPrettyValue?.({ ...child.props, value }) ?? '';
        return fieldPrettyValueFormatter({ label, value: childFormattedValue });
    }
    componentDidMount() {
        const { editing, visible } = this.props;
        editing && this.validate(this.getValue(), false);
        if (this.context) {
            this.unlistenReset = this.context.addResetListener(this.onContextReset);
            this.unlistenValidate = this.context.addValidateListener(this.onContextValidate);
        }
        if (!visible) {
            return;
        }
        this.setValidations();
    }
    shouldComponentUpdate(nextProps, nextState) {
        const { entry, data } = this.props;
        if (shallowCompare(this.props, nextProps, shallowCompareProps)) {
            return true;
        }
        if (shallowCompare(this.state, nextState, shallowCompareState)) {
            return true;
        }
        if (entry) {
            if (get(data, entry) !== get(nextProps.data, entry)) {
                return true;
            }
        }
        else {
            if (data !== nextProps.data) {
                return true;
            }
        }
        return false;
    }
    componentDidUpdate(oldProps) {
        const { data: oldData, editing: oldEditing, localValidators: oldLocalValidators = Field_1.defaultProps.localValidators, validators: oldValidators = Field_1.defaultProps.validators, visible: oldVisible, } = oldProps;
        const { data, dependsOn = Field_1.defaultProps.dependsOn, editing, entry, localValidators = Field_1.defaultProps.localValidators, validators = Field_1.defaultProps.validators, visible, } = this.props;
        const visibleChanged = oldVisible !== visible;
        const shouldSetValidations = visibleChanged && visible;
        const shouldRemoveValidations = visibleChanged && !visible;
        if (shouldSetValidations) {
            this.setValidations();
        }
        if (shouldRemoveValidations) {
            this.removeValidations();
        }
        const allOldValidators = [...Object.values(oldLocalValidators), ...oldValidators];
        const allValidators = [...Object.values(localValidators), ...validators];
        const shouldUpdateEntryValidators = !visibleChanged && visible && !isEqualShallow(allOldValidators, allValidators);
        if (shouldUpdateEntryValidators) {
            this.context?.updateEntryValidators(entry, allValidators);
        }
        if (!oldEditing && editing) {
            this.validate(this.getValue(), this.state.needsValidation);
        }
        if (isEmptyObject(data) && !isEmptyObject(oldData)) {
            this.validate(this.getValue(), this.state.needsValidation);
        }
        if (oldEditing && !editing) {
            this.resetValidations(true);
        }
        if (dependsOn.length > 0) {
            const oldDependsOnData = this.getDependsOnData(oldData);
            const dependsOnData = this.getDependsOnData(data);
            const didChange = Boolean(oldDependsOnData && dependsOnData) && isEqualShallow(oldDependsOnData, dependsOnData);
            if (!didChange) {
                this.onChange(new ChangeEvent({ oldValue: this.getValue(), value: undefined }));
                this.resetValidations(true);
            }
        }
    }
    componentWillUnmount() {
        this.removeValidations();
    }
    setValidations() {
        const { entry, localValidators = {}, validators = [] } = this.props;
        if (!this.context) {
            return;
        }
        const allValidators = [...Object.values(localValidators), ...validators];
        this.context.updateEntryValidators(entry, allValidators);
    }
    removeValidations() {
        this.context?.removeEntryValidators(this.props.entry);
        this.context?.resetEntryChildValidations(this.props.entry);
        if (isFunction(this.unlistenValidate)) {
            this.unlistenValidate();
        }
    }
    getClassName() {
        const { className, orientation = Field_1.defaultProps.orientation, reverse, width = Field_1.defaultProps.width } = this.props;
        return classNames({
            'c-field': true,
            'c-field--horizontal': orientation === FieldOrientation.HORIZONTAL,
            'c-field--reverse': Boolean(reverse),
            [`${className}`]: Boolean(className),
            [`u-col-${width}`]: isValidWidth(width),
        });
    }
    isFieldVisible() {
        const { editing, visibility = Field_1.defaultProps.visibility, visible, } = this.props;
        if (!visible) {
            return false;
        }
        return (visibility === FieldVisibility.BOTH
            || (visibility === FieldVisibility.VIEWING && !editing)
            || (visibility === FieldVisibility.EDITING && Boolean(editing)));
    }
    getDependsOnData(data) {
        if (!data) {
            return;
        }
        const { dependsOn = Field_1.defaultProps.dependsOn } = this.props;
        if (dependsOn.length <= 0) {
            return;
        }
        const result = {};
        for (const field of dependsOn) {
            result[field] = data[field];
        }
        return result;
    }
    getValue() {
        const { data, entry } = this.props;
        return get(data, entry);
    }
    resetValidations(resetChild = false) {
        if (resetChild) {
            this.fieldProviderRef.current?.resetChildValidations();
        }
        this.setState(() => ({ needsValidation: false, showValidations: false }));
    }
    validate(value, showValidations = false, notify = true) {
        if (!this.context) {
            return [true, []];
        }
        const { entry, onValidate } = this.props;
        const [valid, validations] = this.context.validateEntry(entry, value);
        const callback = () => {
            if (!isFunction(onValidate) || !notify) {
                return;
            }
            onValidate(new ValidateEvent({ entry, valid, validations }));
        };
        this.setState({ showValidations, valid }, callback);
        return [valid, validations];
    }
    renderChildren() {
        const { alwaysShowLabel, children, data = Field_1.defaultProps.data, editing, label, onValidate, sensitiveData, strikethroughValue } = this.props;
        const { maskSensitiveValues, showValidations, valid } = this.state;
        const lostProps = getLostProps(Field_1.propTypes, this.props);
        const readOnly = editing === false;
        const inherited = {
            ...lostProps,
            onBlur: this.onBlur,
            onChange: this.onChange,
            onFocus: this.onFocus,
            onKeyDown: this.onKeyDown,
            onKeyUp: this.onKeyUp,
            onValidate,
            readOnly,
            sensitiveData: maskSensitiveValues === false ? SensitiveData.NONE : sensitiveData,
            strikethrough: strikethroughValue,
            valid: valid || !showValidations || !editing,
        };
        /**
         * Setting placeholder this way to not mess with the already set children placeholders
         */
        if (!readOnly && !alwaysShowLabel) {
            inherited.placeholder = label;
        }
        const props = {
            ...inherited,
            value: this.getValue(),
        };
        const dependsOnData = this.getDependsOnData(data);
        if (!isEmptyObject(dependsOnData)) {
            props.dependeeData = dependsOnData;
        }
        return propagateProps(children, props);
    }
    isRequired() {
        const { validators = Field_1.defaultProps.validators } = this.props;
        return validators.some(validator => validator.name === REQUIRED);
    }
    renderToggleHiddenValue() {
        const { discloseableSensitiveData, editing, sensitiveData } = this.props;
        const { maskSensitiveValues } = this.state;
        if (!discloseableSensitiveData || sensitiveData === SensitiveData.NONE || editing) {
            return null;
        }
        return (React.createElement("div", { className: "c-field__label-toggle-privacy", onClick: this.togglePrivateValueMask }, maskSensitiveValues ? React.createElement(ShowIcon, null) : React.createElement(HideIcon, null)));
    }
    renderLabel() {
        const { alwaysShowLabel, editing, ellipsis, hideLabel, information, label = Field_1.defaultProps.label, orientation, strikethroughLabel, } = this.props;
        if (hideLabel || isEmptyOrBlankString(label)) {
            return null;
        }
        const value = this.getValue();
        const isHidden = Boolean(editing)
            && !isContentfulString(value)
            && !alwaysShowLabel
            && !this.isRequired();
        const fieldLabelClassName = classNames({
            'c-field__label': true,
            'c-field__label--ellipsis': Boolean(ellipsis),
        });
        const labelClassName = classNames({
            'c-label': true,
            'is-required': this.isRequired(),
            'u-hidden': isHidden,
            'u-hide': Boolean(hideLabel),
            'u-text-strikethrough': Boolean(strikethroughLabel),
            ...defaultClasses({ readOnly: editing === false }),
        });
        const textValue = MessageService.translate(label);
        return (React.createElement("div", { className: fieldLabelClassName },
            React.createElement(Tooltip, { triggers: [TooltipTrigger.ELLIPSIS], value: textValue },
                React.createElement("label", { className: labelClassName }, textValue)),
            !isEmptyOrBlankString(information) ? (React.createElement(Tooltip, { alignment: Alignment.LEFT, multiline: true, placement: Placement.BOTTOM, value: information },
                React.createElement("div", { className: "c-field__label-icon-container" },
                    React.createElement(InformationCircleIcon, { className: "c-field__label-icon" })))) : null,
            orientation === FieldOrientation.VERTICAL && this.renderToggleHiddenValue()));
    }
    renderHelperText() {
        const { helperText } = this.props;
        const { focused } = this.state;
        if (isEmptyOrBlankString(helperText) || !focused) {
            return null;
        }
        return (React.createElement("span", { className: "c-field__helper-text" }, MessageService.translate(helperText ?? '')));
    }
    renderValidationList() {
        const { editing, localValidators = Field_1.defaultProps.localValidators, validators = Field_1.defaultProps.validators } = this.props;
        const { focused, showValidations, touched } = this.state;
        const allValidators = [...validators, ...Object.values(localValidators)];
        if (!editing || !showValidations) {
            return null;
        }
        const validations = touched
            ? this.context?.getEntryValidations(this.props.entry, this.getValue(), false) ?? []
            : this.context?.getEntryValidations(this.props.entry, this.getValue(), focused) ?? [];
        return (React.createElement(ValidationList, { className: "c-field__validation-list", data: validations, hasFocus: focused, validators: allValidators }));
    }
    render() {
        if (!this.isFieldVisible()) {
            return null;
        }
        const { editing, entry, orientation, uuid } = this.props;
        const className = classNames({
            'c-field__content': true,
            ...defaultClasses({ readOnly: editing === false }),
        });
        return (React.createElement(FieldProvider, { entry: entry, ref: this.fieldProviderRef },
            React.createElement("div", { className: this.getClassName(), "data-testid": "field" },
                React.createElement("div", { className: "c-field__content-wrapper" },
                    this.renderLabel(),
                    React.createElement(Container, { acceptedTypes: FieldContainer.acceptedTypes, className: className, uuid: uuid }, this.renderChildren()),
                    orientation === FieldOrientation.HORIZONTAL && this.renderToggleHiddenValue(),
                    this.renderHelperText()),
                this.renderValidationList())));
    }
};
Field.contextType = FormContext;
Field.propTypes = {
    alwaysShowLabel: bool,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: any,
    dependsOn: arrayOf(string).type('list').group('dependencies'),
    discloseableSensitiveData: bool,
    editing: bool.notEnumerable(),
    ellipsis: bool,
    entry: string,
    helperText: expression,
    hideLabel: bool,
    information: expression,
    label: expression,
    localValidators: object.notEnumerable(),
    onBlur: eventListener,
    onChange: eventListener,
    onKeyUp: eventListener,
    onValidate: eventListener,
    order: number,
    orientation: oneOf(enumValues(FieldOrientation)),
    reverse: bool,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    strikethroughLabel: bool,
    strikethroughValue: bool,
    uuid: string,
    validatedOnChange: bool.group('validation'),
    validators: validators,
    visibility: oneOf(enumValues(FieldVisibility)),
    visible: bool,
    width: number.group('size'),
};
Field.defaultProps = {
    alwaysShowLabel: false,
    data: {},
    dependsOn: [],
    hideLabel: false,
    label: EditorService.isEditor() ? 'Sample label' : '',
    localValidators: {},
    orientation: FieldOrientation.VERTICAL,
    sensitiveData: SensitiveData.NONE,
    uuid: '',
    validatedOnChange: true,
    validators: [],
    visibility: FieldVisibility.BOTH,
    visible: true,
    width: 3,
};
Field = Field_1 = __decorate([
    AwesomeComponent({ api: FieldAPI, containers: [FieldContainer], type: ComponentType.FORM })
], Field);
export { Field, };
export default Field;
//# sourceMappingURL=field.js.map